import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

const scrollLockOwners = new Set();
let savedBodyStyle;
let savedScrollY = 0;

function lockDialogScroll(owner) {
  if (scrollLockOwners.has(owner)) return;
  if (scrollLockOwners.size === 0) {
    const { style } = document.body;
    savedBodyStyle = { width: style.width, position: style.position, top: style.top };
    savedScrollY = window.scrollY;
    style.width = '100%';
    style.position = 'fixed';
    style.top = `-${savedScrollY}px`;
  }
  scrollLockOwners.add(owner);
}

function unlockDialogScroll(owner) {
  if (!scrollLockOwners.delete(owner) || scrollLockOwners.size > 0) return;
  Object.assign(document.body.style, savedBodyStyle);
  savedBodyStyle = undefined;
  window.scrollTo({ top: savedScrollY, behavior: 'instant' });
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];
  #openFrame;
  #session = 0;
  #isOpen = false;
  #closeTask;
  #returnFocusTarget;
  #focusFrame;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('cancel', this.#handleCancel);
    this.refs.dialog.addEventListener('close', this.#handleNativeClose);

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#session++;
    if (this.#focusFrame !== undefined) cancelAnimationFrame(this.#focusFrame);
    this.#focusFrame = undefined;
    this.#returnFocusTarget = undefined;
    this.#cancelPendingOpen();
    this.#handleResize.cancel();
    this.refs.dialog.removeEventListener('cancel', this.#handleCancel);
    this.refs.dialog.removeEventListener('close', this.#handleNativeClose);
    this.#finishClose(false);
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog.
   * @param {HTMLElement | Event} [returnFocusTarget] - Optional opener captured before an asynchronous action disabled it
   */
  showDialog(returnFocusTarget) {
    const { dialog } = this.refs;

    if (!this.isConnected || dialog.open || this.#openFrame !== undefined || this.#closeTask) return;
    if (this.#focusFrame !== undefined) cancelAnimationFrame(this.#focusFrame);
    this.#focusFrame = undefined;
    const currentFocus = document.activeElement;
    const hasCurrentFocus = currentFocus instanceof HTMLElement && currentFocus !== document.body &&
      currentFocus !== document.documentElement && currentFocus.isConnected && !currentFocus.disabled &&
      !currentFocus.closest('[hidden], [inert], [aria-hidden="true"], dialog:not([open])') &&
      currentFocus.getClientRects().length > 0 && getComputedStyle(currentFocus).visibility !== 'hidden';
    const focused = !hasCurrentFocus && returnFocusTarget instanceof HTMLElement && returnFocusTarget.isConnected
      ? returnFocusTarget : currentFocus;
    const parentDialog = focused instanceof Element
      ? focused.closest('dialog-component, quick-add-dialog, cart-drawer-component')
      : null;
    this.#returnFocusTarget = parentDialog instanceof DialogComponent ? parentDialog.returnFocusTarget : focused;
    if (!this.#returnFocusTarget || this.#returnFocusTarget === document.body) {
      this.#returnFocusTarget = [...scrollLockOwners].reverse().find((owner) => owner.returnFocusTarget)?.returnFocusTarget;
    }
    const session = ++this.#session;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    this.#openFrame = requestAnimationFrame(() => {
      if (session !== this.#session || !this.isConnected) return;
      this.#openFrame = undefined;
      dialog.showModal();
      this.#isOpen = true;
      lockDialogScroll(this);
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = () => {
    this.#cancelPendingOpen();
    const { dialog } = this.refs;
    if (this.#closeTask) return this.#closeTask;
    if (!dialog.open) {
      this.#finishClose();
      return Promise.resolve();
    }
    const session = ++this.#session;

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    // Force browser to restart animation by resetting it
    // Temporarily remove any existing animation state
    dialog.style.animation = 'none';

    // Force a reflow
    void dialog.offsetWidth;

    // Now add the closing class and restore animation
    dialog.classList.add('dialog-closing');
    dialog.style.animation = '';

    this.#closeTask = onAnimationEnd(dialog, undefined, { subtree: false }).then(() => {
      if (session !== this.#session) return;
      this.#finishClose();
    });
    return this.#closeTask;
  };

  #cancelPendingOpen() {
    if (this.#openFrame === undefined) return;
    cancelAnimationFrame(this.#openFrame);
    this.#openFrame = undefined;
    this.#session++;
  }

  #finishClose(notify = true) {
    const { dialog } = this.refs;
    const wasOpen = this.#isOpen;
    this.#isOpen = false;
    this.#closeTask = undefined;
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    if (dialog.open) dialog.close();
    dialog.classList.remove('dialog-closing');
    unlockDialogScroll(this);
    if (notify && wasOpen) {
      this.dispatchEvent(new DialogCloseEvent());
      this.#restoreFocus();
    }
  }

  get returnFocusTarget() {
    return this.#returnFocusTarget;
  }

  /** Original page offset while this dialog owns the shared body lock. */
  get scrollLockY() {
    return scrollLockOwners.has(this) ? savedScrollY : undefined;
  }

  #restoreFocus() {
    const session = this.#session;
    if (this.#focusFrame !== undefined) cancelAnimationFrame(this.#focusFrame);
    this.#focusFrame = requestAnimationFrame(() => {
      this.#focusFrame = undefined;
      if (!this.isConnected || session !== this.#session || document.querySelector('dialog[open]')) return;
      const visible = (element) => element instanceof HTMLElement && element.isConnected && !element.disabled &&
        !element.closest('[hidden], [inert], [aria-hidden="true"], dialog:not([open])') &&
        element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
      const focused = document.activeElement;
      if (focused !== document.body && focused !== document.documentElement && visible(focused)) return;
      if (visible(this.#returnFocusTarget)) this.#returnFocusTarget.focus({ preventScroll: true });
    });
  }

  #handleCancel = (event) => {
    event.preventDefault();
    this.closeDialog();
  };

  #handleNativeClose = () => {
    if (this.refs.dialog.open || !this.#isOpen) return;
    this.#session++;
    this.#finishClose();
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        if (document.querySelector('details[scroll-lock][open]')) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
